import {
  CourseRecommendationsSchema,
  CourseLaunchResponseSchema,
  CourseAuthResponseSchema,
} from "@artha/api-contracts";
import { LEARNING, LEARNING_ERROR, LEARNING_PATH } from "../learning.constants";

/** Exposes transport status without displaying unlocalized upstream messages. */
export class LearningError extends Error {
  constructor(public readonly status: number) {
    super(LEARNING_ERROR.unavailable);
  }
}

/** Same-origin gateway keeps provider credentials and sessions out of browser code. */
export async function learningRequest<T = unknown>(
  path: string,
  body?: unknown,
  signal?: AbortSignal,
): Promise<T> {
  const response = await fetch(`${LEARNING.apiPrefix}${path}`, {
    method: body === undefined ? "GET" : "POST",
    credentials: "same-origin",
    cache: "no-store",
    signal,
    headers: { "content-type": "application/json" },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (!response.ok) throw new LearningError(response.status);
  return response.json();
}

/** Bounded recommendations are cached per job and creator by the query hook. */
export async function recommendCourses(
  jobId: string,
  creatorHandle: string,
  signal?: AbortSignal,
  cursor?: string,
) {
  return CourseRecommendationsSchema.parse(
    await learningRequest(
      LEARNING_PATH.recommendations,
      {
        job_id: jobId,
        creator_handle: creatorHandle,
        sort: LEARNING.recommended,
        limit: LEARNING.limit,
        ...(cursor ? { cursor } : {}),
      },
      signal,
    ),
  );
}

/** Keep a stable request ID when retrying the same course selection. */
export async function launchCourse(
  recommendationToken: string,
  requestId: string,
) {
  const result = CourseLaunchResponseSchema.parse(
    await learningRequest(LEARNING_PATH.launches, {
      recommendation_token: recommendationToken,
      request_id: requestId,
    }),
  );
  const target = new URL(result.launch_url, window.location.origin);
  if (
    target.origin !== window.location.origin ||
    !target.pathname.startsWith(LEARNING.launchPrefix)
  ) {
    throw new Error(LEARNING_ERROR.invalidLaunch);
  }
  window.location.assign(target.href);
}

/** Starts or verifies the shared candidate OTP through the trusted gateway. */
export async function authenticateLearner(path: string, input: unknown) {
  return CourseAuthResponseSchema.parse(await learningRequest(path, input));
}
